import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import {
  AuthControllerLogin200Response,
  AuthControllerLogout200Response,
  AuthControllerResetPassword200Response,
  AuthControllerResetPasswordAction200Response,
  AuthControllerSignup200Response,
  LoginDto,
  ResetPasswordActionDto,
  ResetPasswordDto,
  SignupDto,
  UserDto
} from "@ennova-research/waveup-user-service-sdk";
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  tap
} from "rxjs";
import { environment } from "../../../../environments/environment";
import { ROUTE } from "../../../shared/utils/routes";
import { getContext } from "../../interceptors/loader/loader.interceptor";
import { UsersProfile } from "../../types/users-profile.types";
import { UsersService } from "../users/users.service";

// Local storage field to keep track of onBehalfOf id to use during page f5
export const onBehalfOfUserIdField = "onBehalfOfUserId" as const;

@Injectable({
  providedIn: "root"
})
export class AuthService {
  private readonly baseUrl = `${environment.apiGatewayUrl}/auth`;
  private loggedUser: UserDto | null = null;
  // undefined = login not execute , null = login execute and onBeHalfOf no value.
  // for trigger login for onBeHalfOfUser set undefined
  private _onBeHalfOfUser: UserDto | null | undefined = undefined;

  set onBeHalfOfUser(user: UserDto | null | undefined) {
    this._onBeHalfOfUser = user;

    if (user?.id) {
      localStorage.setItem(onBehalfOfUserIdField, user.id);
    } else {
      localStorage.removeItem(onBehalfOfUserIdField);
    }
  }

  get onBeHalfOfUser(): UserDto | null | undefined {
    return this._onBeHalfOfUser;
  }

  get onBeHalfOfUserId(): string | undefined {
    return (
      this._onBeHalfOfUser?.id ??
      localStorage.getItem(onBehalfOfUserIdField) ??
      undefined
    );
  }

  private onBeHalfOfUserSubject = new Subject<UserDto | null | undefined>();
  onBeHalfOfUserEvent = this.onBeHalfOfUserSubject.asObservable();

  private getCurrentUserAndOnBehalfOfUserDoneSubject =
    new BehaviorSubject<boolean>(false);
  private isGetCurrentUserAndOnBehalfOfUserInProgress = false;

  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly usersService = inject(UsersService);

  /**
   * Logins the user if given credentials, otherwise refreshes the token
   * @param loginDto
   */
  login(
    loginDto: LoginDto = {},
    showLoader = true
  ): Observable<AuthControllerLogin200Response> {
    return this.http.post<AuthControllerLogin200Response>(
      `${this.baseUrl}/login`,
      loginDto,
      { context: getContext(showLoader) }
    );
  }

  isAuthenticated$(): Observable<boolean> {
    return this.getCurrentUser().pipe(
      map((user) => !!user),
      catchError(() => of(false))
    );
  }

  private getCurrentUser(
    forceReset = false,
    showLoader = true
  ): Observable<UserDto | null> {
    if (this.loggedUser && !forceReset) {
      return of(this.loggedUser);
    } else {
      return this.usersService.getProfile(showLoader).pipe(
        map((response) => response.data),
        catchError(() => of(null)),
        tap((user) => (this.loggedUser = user))
      );
    }
  }

  private getOnBehalfOfUser(forceReset = false): Observable<UserDto | null> {
    if (this.onBeHalfOfUser !== undefined && !forceReset) {
      return of(this.onBeHalfOfUser);
    } else {
      return this.usersService.getOnBehalfOfProfile().pipe(
        map((response) => {
          if (response.data) {
            return response.data;
          }
          return null;
        }),
        tap((user) => {
          this.onBeHalfOfUser = user;
          this.onBeHalfOfUserSubject.next(user);
        })
      );
    }
  }

  getCurrentUserAndOnBehalfOfUser(
    forceReset = false,
    showLoader = true
  ): Observable<UsersProfile> {
    // Quickfix to avoid doing 5 calls to /profile-on-behalf-of at the same time
    if (this.isGetCurrentUserAndOnBehalfOfUserInProgress && !forceReset) {
      return this.getCurrentUserAndOnBehalfOfUserDoneSubject.pipe(
        filter((value) => value === true),
        take(1),
        switchMap(() => {
          return of({
            currentUser: this.loggedUser,
            onBehalfOfUser: this.onBeHalfOfUser ?? null
          });
        })
      );
    }

    this.isGetCurrentUserAndOnBehalfOfUserInProgress = true;
    this.getCurrentUserAndOnBehalfOfUserDoneSubject.next(false);
    return this.getCurrentUser(forceReset, showLoader).pipe(
      switchMap((currentUser) => {
        if (currentUser?.role === "tem") {
          return this.getOnBehalfOfUser(forceReset).pipe(
            map((onBehalfOfUser) => ({
              currentUser,
              onBehalfOfUser
            }))
          );
        }
        return of({ currentUser: currentUser, onBehalfOfUser: null });
      }),
      finalize(() => {
        this.isGetCurrentUserAndOnBehalfOfUserInProgress = false;
        this.getCurrentUserAndOnBehalfOfUserDoneSubject.next(true);
      })
    );
  }

  clearOnBeHalfOfUser(): void {
    this.onBeHalfOfUser = undefined;
    this.onBeHalfOfUserSubject.next(undefined);
  }

  logout(): Observable<AuthControllerLogout200Response> {
    this.loggedUser = null;
    this.clearOnBeHalfOfUser();

    return this.http
      .post<AuthControllerLogout200Response>(`${this.baseUrl}/logout`, {})
      .pipe(
        finalize(() => {
          this.router.navigate([ROUTE.LOGIN]);
        })
      );
  }

  signup(
    signupDto: SignupDto,
    showLoader = true
  ): Observable<AuthControllerSignup200Response> {
    return this.http.post<AuthControllerSignup200Response>(
      `${this.baseUrl}/signup`,
      signupDto,
      { context: getContext(showLoader) }
    );
  }

  askResetPassword(
    payload: ResetPasswordDto
  ): Observable<AuthControllerResetPassword200Response> {
    return this.http.post<AuthControllerResetPassword200Response>(
      `${this.baseUrl}/reset-password`,
      payload
    );
  }
  resetPassword(
    payload: ResetPasswordActionDto
  ): Observable<AuthControllerResetPasswordAction200Response> {
    return this.http.put<AuthControllerResetPasswordAction200Response>(
      `${this.baseUrl}/reset-password`,
      payload
    );
  }
}
